'use client';

import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { ExternalLink, LogOut, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useAuth } from '@/components/auth/AuthProvider';
import { ADMIN_MODULES } from '@ciptaimaji/shared';
import { ROLE_LABEL } from '@/components/customer/labels';
import { ADMIN_NAV, navItemFor, type AdminNavItem } from './nav';
import { LoginScreen } from './LoginScreen';

const NAV_RAIL_KEY = 'ci-admin-nav-rail';

/**
 * Teks dan tombol yang hanya muat saat sidebar lebar: tetap di DOM supaya tata
 * letak tidak berubah (tidak ada lompatan), hanya memudar. Muncul sedikit
 * belakangan agar lebarnya sempat bergerak dulu, dan hilang seketika.
 */
function fade(compact: boolean): string {
  return compact
    ? 'pointer-events-none opacity-0 transition-opacity duration-100'
    : 'opacity-100 transition-opacity delay-100 duration-200';
}

function isActive(item: AdminNavItem, pathname: string): boolean {
  if (item.exact) return pathname === item.href;
  return pathname === item.href || pathname.startsWith(`${item.href}/`);
}

export function AdminShell({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const { user, loading, logout, access, can, refreshAccess } = useAuth();
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  // Sidebar bawaannya rail ikon dan melebar saat disentuh tetikus; pilihan
  // mengunci lebar penuh disimpan per browser.
  const [rail, setRail] = useState(true);
  const [hovering, setHovering] = useState(false);

  useEffect(() => {
    try {
      setRail(localStorage.getItem(NAV_RAIL_KEY) !== '0');
    } catch {
      // localStorage bisa diblokir browser; pakai rail seperti bawaan.
    }
  }, []);

  const toggleRail = () => {
    setHovering(false);
    setRail((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(NAV_RAIL_KEY, next ? '1' : '0');
      } catch {
        // abaikan
      }
      return next;
    });
  };

  const compact = rail && !hovering;

  if (loading) {
    return (
      <main className="grid min-h-dvh place-items-center bg-charcoal">
        <p className="text-[13px] text-white/80">Memeriksa sesi…</p>
      </main>
    );
  }

  if (!user) return <LoginScreen />;

  if (user.role === 'CUSTOMER') {
    return (
      <LoginScreen notice="Akun ini tidak punya akses pengelolaan. Masuk dengan akun admin atau staf." />
    );
  }

  if (!access) {
    return (
      <main className="grid min-h-dvh place-items-center bg-charcoal">
        <div className="text-center">
          <p className="text-[13px] text-white/85">Hak akses akun ini gagal dimuat.</p>
          <div className="mt-3 flex justify-center gap-2">
            <button
              type="button"
              onClick={() => void refreshAccess()}
              className="rounded-md bg-lime px-4 py-2 text-[12.5px] font-bold text-charcoal"
            >
              Coba lagi
            </button>
            <button
              type="button"
              onClick={() => void logout()}
              className="rounded-md border border-white/25 px-4 py-2 text-[12.5px] font-semibold text-white/85"
            >
              Keluar
            </button>
          </div>
        </div>
      </main>
    );
  }

  const isSuper = user.role === 'SUPER_ADMIN';
  const allowed = (item: AdminNavItem): boolean =>
    item.module === null ? isSuper : can(item.module, 'VIEW');
  const visibleNav = ADMIN_NAV.map((group) => ({
    ...group,
    items: group.items.filter(allowed),
  })).filter((group) => group.items.length > 0);

  const current = navItemFor(pathname);
  const blocked = current !== null && !allowed(current);
  const currentModule = current?.module
    ? ADMIN_MODULES.find((mod) => mod.key === current.module)
    : undefined;
  const viewOnly =
    !blocked && currentModule?.manageable === true && !can(currentModule.key, 'MANAGE');

  const toggleGroup = (label: string) => {
    setCollapsed((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  return (
    <div className="admin-shell" data-nav={rail ? 'rail' : 'full'}>
      <aside
        className="admin-nav flex flex-col overflow-hidden border-r border-white/10 bg-charcoal"
        data-open={rail && hovering ? '1' : '0'}
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
        onFocus={() => setHovering(true)}
        onBlur={(e) => {
          // Fokus yang pindah ke dalam sidebar tidak boleh menutupnya.
          if (!e.currentTarget.contains(e.relatedTarget)) setHovering(false);
        }}
      >
        {/* Tata letaknya sengaja sama di kedua lebar: yang berubah hanya opacity,
            jadi ikon tidak pernah melompat saat sidebar melebar. */}
        <div className="flex shrink-0 items-center justify-between gap-1 overflow-hidden pt-3 pr-2.5 pb-4 pl-[18px]">
          {/* shrink-0: di lebar rail logo tidak boleh ikut menyusut jadi nol. */}
          <Link href="/" title="CIPTA IMAJI" className="flex shrink-0 items-center gap-2.5 py-1">
            <Image
              src="/brand/ci-mark.webp"
              unoptimized
              alt=""
              width={129}
              height={192}
              priority
              className="h-8 w-auto shrink-0"
            />
            <span
              className={`truncate font-display text-base whitespace-nowrap text-clickwhite ${fade(compact)}`}
            >
              CIPTA IMAJI
            </span>
          </Link>
          <button
            type="button"
            onClick={toggleRail}
            aria-label={rail ? 'Kunci sidebar terbuka' : 'Ringkas sidebar jadi ikon'}
            aria-expanded={!rail}
            title={rail ? 'Kunci sidebar terbuka' : 'Ringkas sidebar jadi ikon'}
            className={`grid size-8 shrink-0 place-items-center rounded-md text-white/70 transition-colors hover:bg-white/8 hover:text-clickwhite ${fade(compact)}`}
          >
            {rail ? (
              <PanelLeftOpen aria-hidden className="size-[18px]" />
            ) : (
              <PanelLeftClose aria-hidden className="size-[18px]" />
            )}
          </button>
        </div>

        {/* Hanya daftar menu yang bergulir; logo di atas dan akun di bawah tetap di tempat. */}
        <nav
          aria-label="Menu admin"
          className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto px-2.5 pb-2"
        >
          {visibleNav.map((group) => {
            // Saat ringkas semua grup terbuka: judulnya tidak terbaca, jadi menu
            // yang tersembunyi di balik grup tertutup akan sulit dicari.
            const isCollapsed = !compact && collapsed[group.label];
            return (
              <div key={group.label} className="mb-3">
                <button
                  type="button"
                  onClick={() => toggleGroup(group.label)}
                  tabIndex={compact ? -1 : undefined}
                  aria-hidden={compact}
                  className={`mb-1 flex w-full items-center justify-between px-2 text-[10px] font-semibold whitespace-nowrap text-white/80 uppercase hover:text-white/90 ${fade(compact)}`}
                >
                  <span>{group.label}</span>
                  <svg
                    aria-hidden
                    viewBox="0 0 20 20"
                    className={`size-3 shrink-0 transition-transform duration-200 ${isCollapsed ? '-rotate-90' : ''}`}
                  >
                    <path
                      d="M5 7.5L10 12.5L15 7.5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <div
                  className="grid transition-[grid-template-rows] duration-200 ease-out"
                  style={{ gridTemplateRows: isCollapsed ? '0fr' : '1fr' }}
                >
                  <div className="flex flex-col gap-0.5 overflow-hidden">
                    {group.items.map((item) => {
                      const Icon = item.icon;
                      const layout = 'flex items-center gap-2.5 rounded-md px-2.5 py-2 text-[13px]';
                      const body = (
                        <>
                          <Icon aria-hidden className="size-4 shrink-0" strokeWidth={2} />
                          <span className={`truncate whitespace-nowrap ${fade(compact)}`}>
                            {item.label}
                          </span>
                        </>
                      );
                      if (item.soon) {
                        return (
                          <span
                            key={item.label}
                            title={`${item.label} (segera hadir)`}
                            className={`${layout} font-semibold text-white/60`}
                          >
                            {body}
                          </span>
                        );
                      }
                      const active = isActive(item, pathname);
                      return (
                        <Link
                          key={item.href}
                          href={item.href}
                          title={compact ? item.label : undefined}
                          aria-current={active ? 'page' : undefined}
                          className={
                            active
                              ? `${layout} bg-white/8 font-bold text-lime`
                              : `${layout} font-semibold text-white/90 transition-colors hover:bg-white/5 hover:text-clickwhite`
                          }
                        >
                          {body}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </nav>

        <div className="shrink-0 border-t border-white/15 bg-charcoal px-2.5 pt-2 pb-3">
          <a
            href="https://ciptaimaji.id"
            target="_blank"
            rel="noreferrer"
            title={compact ? 'Lihat situs' : undefined}
            className="flex items-center gap-2.5 rounded-md px-2.5 py-2 text-[12px] font-semibold text-white/80 hover:text-clickwhite"
          >
            <ExternalLink aria-hidden className="size-4 shrink-0" />
            <span className={`whitespace-nowrap ${fade(compact)}`}>Lihat situs</span>
          </a>

          <div className="mt-2 flex items-center gap-2 rounded-md bg-white/5 px-2 py-2">
            <span
              aria-hidden
              title={compact ? `${user.name} · ${ROLE_LABEL[user.role]}` : undefined}
              className="grid size-7 shrink-0 place-items-center rounded-full bg-lime text-[12px] font-bold text-charcoal"
            >
              {user.name.slice(0, 1).toUpperCase()}
            </span>
            <span className={`min-w-0 flex-1 leading-tight whitespace-nowrap ${fade(compact)}`}>
              <b className="block truncate text-[12.5px] text-clickwhite">{user.name}</b>
              <span className="block truncate text-[10.5px] font-semibold text-white/60">
                {ROLE_LABEL[user.role]}
              </span>
            </span>
            <button
              type="button"
              onClick={() => void logout()}
              aria-label="Keluar"
              title="Keluar"
              tabIndex={compact ? -1 : undefined}
              className={`grid size-8 shrink-0 place-items-center rounded-md text-white/70 transition-colors hover:bg-white/10 hover:text-clickwhite ${fade(compact)}`}
            >
              <LogOut aria-hidden className="size-4" />
            </button>
          </div>
        </div>
      </aside>

      <div className="admin-main flex flex-col overflow-hidden bg-[#f6f7f9]">
        {viewOnly ? (
          <p className="shrink-0 border-b border-black/10 bg-lime/25 px-5 py-1.5 text-[12px] font-semibold text-charcoal">
            Mode lihat saja: akun Anda bisa membuka {currentModule?.label ?? 'menu ini'}, tetapi
            perubahan akan ditolak.
          </p>
        ) : null}
        <div className="min-h-0 flex-1 overflow-auto">
          {blocked ? (
            <div className="p-7">
              <h1 className="font-display text-[26px] text-charcoal">Tidak ada akses</h1>
              <p className="mt-1.5 max-w-[52ch] text-[13px] text-charcoal/75">
                Akun {ROLE_LABEL[user.role].toLowerCase()} tidak diberi akses ke{' '}
                {current?.label ?? 'halaman ini'}. Minta super admin mengubah hak akses bila perlu.
              </p>
            </div>
          ) : (
            children
          )}
        </div>
      </div>
    </div>
  );
}
